import baseAxios, { AxiosResponse } from 'axios';
import {
  BusinessSettings,
  BusinessTaxRates,
  ShopOnboardingID,
} from '@prisma/client';
import type Stripe from 'stripe';
import API_URL from '../apiconfig';
import {
  Business,
  Employee,
  FilterOption,
  LoadedUser,
  Service,
  Trip,
} from '../interfaces';
import { getArtistInitials, getArtistName } from '../utils/global/helper';
import axios from './api';
import {
  AddBusinessTripPayload,
  BusinessService,
  BusinessTaxRatesPayload,
  ChiliPiperResponse,
  CompleteOnboardingStepResponse,
  OnboardingResponse,
  SkipOnboardingStepResponse,
  TerminalProducts,
  UpdateBusinessPayload,
  UpdateBusinessSettingsPayload,
  UpsertBusinessServiceDto,
} from '../types/businesses';
import { Resource } from '../types/calendar';
import {
  GetStripeAccountResponse,
  LinkStripeAccountDto,
  LinkStripeAccountResponse,
} from '../types/stripe';
import client from '../utils/react-query/client';
import { ResourceBlock } from '../types/employees';

export async function getBusiness(businessId: string) {
  try {
    const business = await axios.get(`${API_URL}/v1/businesses/${businessId}`);

    return business.data.result;
  } catch (error) {
    throw new Error('Cannot get business');
  }
}

export async function getTerminalReader(businessId: string) {
  try {
    const reader = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/terminal-reader`,
    );

    return reader.data.result as { reader: Stripe.Terminal.Reader };
  } catch (error) {
    throw new Error('Cannot get business');
  }
}

export async function getTerminalProducts() {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/terminal-reader/products`,
    );

    return response.data.result as TerminalProducts;
  } catch (error) {
    throw new Error('Cannot get terminal products');
  }
}

export async function initatePurchaseTerminalCheckout({
  priceId,
  businessId,
  successUrl,
}: {
  priceId: string;
  businessId: string;
  successUrl: string;
}) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/terminal-reader/checkout`,
      {
        priceId,
        successUrl,
      },
    );

    return response.data.result as Stripe.Checkout.Session;
  } catch (error) {
    throw new Error('Cannot initiate purchase terminal checkout');
  }
}

export async function cancelCurrentTerminalReaderAction(
  businessId: string,
  readerId: string,
): Promise<AxiosResponse> {
  const response = await axios
    .patch(`${API_URL}/v1/businesses/${businessId}/cancel-terminal-checkout`, {
      readerId,
    })
    .catch(error => {
      throw new Error(error.response.data.message);
    });
  return response;
}

export const getEmployeeToBusiness = (
  loadedUser: LoadedUser,
  employeeId: string,
) => loadedUser.business.employees.find(etb => etb.employeeId === employeeId);

export const generateColorForArtist = (
  artistId: string,
  loadedUser: LoadedUser,
): string => {
  const employeeToBusiness = getEmployeeToBusiness(loadedUser, artistId);
  if (employeeToBusiness?.color) {
    return employeeToBusiness.color;
  }

  const vibrantHues = [0, 15, 30, 45, 120, 150, 195, 220, 260, 280, 330];
  const artistHash = Array.from(artistId).reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0,
  );

  const hue = vibrantHues[artistHash % vibrantHues.length];

  // Slightly lower maximum saturation for a bit less brightness
  const saturation = 70 + (artistHash % 21); // Values between 70% and 90%

  // A bit higher minimum lightness to reduce brightness slightly
  const lightness = 45 + (artistHash % 16); // Values between 45% and 60%

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

export function getArtistOptions(
  user: LoadedUser,
  includeFirstAvailable?: boolean,
) {
  const { business } = user;

  const artistOptions: FilterOption[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        employee.isBookable ||
        user.business.services?.find(service =>
          service.employeesToServices.find(
            employeeToService =>
              employeeToService.employeeId === employee.employeeId,
          ),
        )
      ) {
        artistOptions.push({
          label: getArtistName(employee),
          value: employee.employeeId,
          color: generateColorForArtist(employee.employeeId, user),
        });
      }
    });
  }

  if (includeFirstAvailable) {
    artistOptions.unshift({
      label: 'First Available',
      value: 'first-available',
      color: '#000000',
    });
  }

  return artistOptions;
}

export async function getArtistsForService(user: LoadedUser, service: Service) {
  const { business } = user;

  const artistOptions: FilterOption[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        service.employeesToServices.find(
          mapping => mapping.employeeId === employee.employeeId,
        )
      ) {
        artistOptions.push({
          label: getArtistName(employee),
          value: employee.employeeId,
          color: generateColorForArtist(employee.employeeId, user),
        });
      }
    });
  }
  return artistOptions;
}

export function getResourceMap(user: LoadedUser) {
  const { business } = user;

  const resourceMap: Resource[] = [];
  if (business.employees) {
    business.employees.forEach(employee => {
      if (
        employee.isBookable ||
        user.business.services?.find(service =>
          service.employeesToServices.find(
            employeeToService =>
              employeeToService.employeeId === employee.employeeId,
          ),
        )
      ) {
        resourceMap.push({
          resourceId: employee.employeeId,
          resourceTitle: employee.displayName || getArtistInitials(employee),
          color: generateColorForArtist(employee.employeeId, user),
        });
      }
    });
  }
  return resourceMap;
}

export const getEmployeesResourcesList = (user: LoadedUser) => {
  if (user.role === 'EMPLOYEE') {
    return [
      {
        resourceId: user.userId,
        resourceTitle: user.employee.displayName || '',
        color: generateColorForArtist(user.userId, user),
      },
    ];
  }

  return getResourceMap(user);
};

export async function removeEmployeeFromShop(
  employee: Employee,
  businessId: string,
) {
  try {
    const result = await axios.delete(
      `${API_URL}/v1/businesses/${businessId}/${employee.employeeId}`,
    );

    return result;
  } catch (error) {
    throw new Error('Cannot remove employee');
  }
}

export async function updateBusiness(
  businessId: string,
  payload: UpdateBusinessPayload,
) {
  const response = await axios.patch(
    `${API_URL}/v1/businesses/${businessId}`,
    payload,
  );

  return response?.data?.result as Business;
}

export async function updateBusinessTaxRates(
  businessId: string,
  payload: BusinessTaxRatesPayload,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/tax-rates`,
      payload,
    );

    return response?.data?.result as BusinessTaxRates;
  } catch (error) {
    throw new Error('Cannot update business');
  }
}

export async function addBusinessTrip(
  businessId: string,
  payload: AddBusinessTripPayload,
) {
  try {
    const response = await axios.post(`${API_URL}/v1/trips/business-trip`, {
      ...payload,
      businessId,
    });

    return response?.data?.result as Trip;
  } catch (error) {
    throw new Error('Cannot add trip');
  }
}

export async function getBusinessSettings(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/settings`,
    );

    return response?.data?.result as BusinessSettings;
  } catch (error) {
    throw new Error('Cannot get business settings');
  }
}

export async function updateBusinessSettings(
  businessId: string,
  payload: Omit<UpdateBusinessSettingsPayload, 'bookInAdvanceHours'> & {
    bookInAdvanceMinutes: number;
  },
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/settings`,
      payload,
    );

    return response?.data?.result as BusinessSettings;
  } catch (error) {
    throw new Error('Cannot update business settings');
  }
}

export async function getBusinessServices(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/services`,
    );

    return response?.data?.result as BusinessService[];
  } catch (error) {
    throw new Error('Cannot get business services');
  }
}

export async function createBusinessService(
  businessId: string,
  body: UpsertBusinessServiceDto,
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/services`,
      body,
    );

    return response?.data?.result as BusinessService;
  } catch (error) {
    throw new Error('Cannot create business service');
  }
}

export async function updateBusinessService(
  businessId: string,
  serviceId: number,
  body: UpsertBusinessServiceDto,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/services/${serviceId}`,
      body,
    );

    return response?.data?.result as BusinessService;
  } catch (error) {
    throw new Error('Cannot update business service');
  }
}

export async function invalidateBusinessServices() {
  await client.invalidateQueries({
    queryKey: ['businessServices'],
    exact: false,
  });
}
export async function getBusinessStripeAccount(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/stripe-account`,
    );

    return response?.data?.result as GetStripeAccountResponse;
  } catch (error) {
    throw new Error('Cannot get business stripe account');
  }
}

export async function linkBusinessStripeAccount(
  businessId: string,
  payload: LinkStripeAccountDto,
) {
  const response = await axios.post(
    `${API_URL}/v1/businesses/${businessId}/stripe-account/link`,
    payload,
  );

  return response?.data?.result as LinkStripeAccountResponse;
}

export async function loginBusinessStripeAccount(businessId: string) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/stripe-account/login`,
    );

    return response?.data?.result as LinkStripeAccountResponse;
  } catch (error) {
    throw new Error('Cannot get stripe login link');
  }
}

export async function getBusinessOnboarding(businessId: string) {
  try {
    const response = await axios.get(
      `${API_URL}/v1/businesses/${businessId}/onboarding`,
    );

    return response?.data?.result as OnboardingResponse;
  } catch (error) {
    throw new Error('Cannot get business onboarding');
  }
}

export async function skipOnboardingStep(
  businessId: string,
  stepId: ShopOnboardingID,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/onboarding/${stepId}/skip`,
    );

    return response?.data?.result as SkipOnboardingStepResponse;
  } catch (error) {
    throw new Error('Cannot skip onboarding step');
  }
}

export async function completeOnboardingStep(
  businessId: string,
  stepId: ShopOnboardingID,
) {
  try {
    const response = await axios.patch(
      `${API_URL}/v1/businesses/${businessId}/onboarding/${stepId}/complete`,
    );

    return response?.data?.result as CompleteOnboardingStepResponse;
  } catch (error) {
    throw new Error('Cannot complete onboarding step');
  }
}

export async function getOnboardingSpecialistUrl({
  firstName,
  lastName,
  email,
  company,
}: {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
}) {
  try {
    const url =
      'https://getporter.chilipiper.com/concierge-router/onboarding_router/rest';
    const body = {
      form: {
        PersonFirstName: firstName,
        PersonLastName: lastName,
        CompanyName: company,
        PersonEmail: email,
      },
      options: {
        router: 'onboarding_router',
      },
    };

    const response = await baseAxios.post(url, body);

    return response.data as ChiliPiperResponse;
  } catch {
    // Empty
    return null;
  }
}

export async function createShopTimeBlocks(
  businessId: string,
  payload: ResourceBlock & {
    employeeIds: string[];
  },
) {
  try {
    const response = await axios.post(
      `${API_URL}/v1/businesses/${businessId}/resource-blocks`,
      payload,
    );
    return response.data.result as string[];
  } catch (error) {
    throw new Error('Cannot create time block');
  }
}
